import React, { useEffect, useState } from 'react';
import { fetchScorecardData } from '../api/monday';

// ── Helpers ────────────────────────────────────────────────────────────────

function formatWeekOf(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return new Date(Number(y), Number(m) - 1, Number(d))
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function shortName(fullName) {
  // "Matt Mellott" → "Matt M."
  const parts = fullName.trim().split(' ');
  if (parts.length === 1) return fullName;
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
}

function clearanceColor(pct) {
  if (pct >= 80) return { color: '#16a34a', bg: '#dcfce7' };
  if (pct >= 60) return { color: '#d97706', bg: '#fffbeb' };
  return { color: '#dc2626', bg: '#fef2f2' };
}

function overdueColor(count) {
  if (count === 0) return { color: '#16a34a', bg: '#dcfce7' };
  if (count === 1) return { color: '#d97706', bg: '#fffbeb' };
  return { color: '#dc2626', bg: '#fef2f2' };
}

// ── Styles ─────────────────────────────────────────────────────────────────

const s = {
  page: {
    fontFamily: "'Inter', 'Segoe UI', system-ui, Arial, sans-serif",
    maxWidth: '720px',
    margin: '0 auto',
    minHeight: '100vh',
    backgroundColor: '#f5f4f1',
    display: 'flex',
    flexDirection: 'column',
  },

  nav: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px',
    backgroundColor: '#fff',
    borderBottom: '1px solid #e8e6e0',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  navTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#6b7280',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
  },
  navToggle: {
    display: 'flex',
    gap: '6px',
  },
  toggleBtn: {
    padding: '6px 14px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    border: '1px solid #e5e7eb',
    transition: 'all 0.12s',
  },

  body: { flex: 1, padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' },

  card: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },

  // Header row inside card
  cardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '12px',
  },
  cardTitle: {
    fontSize: '17px',
    fontWeight: '700',
    color: '#111827',
    margin: 0,
  },
  periodTabs: {
    display: 'flex',
    gap: '6px',
  },
  periodTab: {
    padding: '5px 12px',
    borderRadius: '7px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    border: '1px solid #e5e7eb',
    transition: 'all 0.12s',
  },

  // Stat tiles
  tileGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '12px',
    marginBottom: '24px',
  },
  tile: {
    borderRadius: '10px',
    padding: '14px',
    textAlign: 'center',
    backgroundColor: '#f9fafb',
  },
  tileNumber: {
    fontSize: '28px',
    fontWeight: '800',
    lineHeight: 1,
    marginBottom: '4px',
  },
  tileLabel: {
    fontSize: '11px',
    fontWeight: '600',
    color: '#6b7280',
    lineHeight: '1.3',
  },

  // Table
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    fontSize: '11px',
    fontWeight: '700',
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    textAlign: 'right',
    padding: '0 8px 10px',
    borderBottom: '1px solid #f3f4f6',
  },
  thLeft: {
    textAlign: 'left',
    padding: '0 8px 10px',
    borderBottom: '1px solid #f3f4f6',
  },
  td: {
    padding: '12px 8px',
    fontSize: '14px',
    color: '#374151',
    textAlign: 'right',
    borderBottom: '1px solid #f9fafb',
    fontWeight: '500',
  },
  tdLeft: {
    padding: '12px 8px',
    fontSize: '14px',
    color: '#111827',
    fontWeight: '600',
    borderBottom: '1px solid #f9fafb',
    textAlign: 'left',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '28px',
    padding: '2px 8px',
    borderRadius: '99px',
    fontSize: '12px',
    fontWeight: '700',
  },

  footer: {
    fontSize: '11px',
    color: '#9ca3af',
    marginTop: '16px',
    textAlign: 'center',
  },

  loading: { textAlign: 'center', padding: '60px 20px', color: '#9ca3af', fontSize: '15px' },
  error: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '16px', color: '#b91c1c', fontSize: '14px' },
};

// ── Main component ─────────────────────────────────────────────────────────

export default function Scorecard({ onBack }) {
  const [period, setPeriod] = useState('week');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchScorecardData(period)
      .then((d) => { setData(d); setLoading(false); })
      .catch((err) => { setError(err.message || 'Failed to load scorecard.'); setLoading(false); });
  }, [period]);

  const periodLabel = period === 'week'
    ? `week of ${formatWeekOf(data?.startDate)}`
    : `month of ${formatWeekOf(data?.startDate?.slice(0, 8) + '01')}`;

  return (
    <div style={s.page}>
      {/* Nav */}
      <div style={s.nav}>
        <span style={s.navTitle}>SterlingCRE — Prospect + Nurture</span>
        <div style={s.navToggle}>
          <button
            style={{
              ...s.toggleBtn,
              backgroundColor: '#fff',
              color: '#374151',
            }}
            onClick={onBack}
          >
            Queue view
          </button>
          <button
            style={{
              ...s.toggleBtn,
              backgroundColor: '#534AB7',
              color: '#fff',
              border: '1px solid #534AB7',
            }}
          >
            Scorecard
          </button>
        </div>
      </div>

      <div style={s.body}>
        {loading && <div style={s.loading}>Loading scorecard…</div>}
        {error && <div style={s.error}><strong>Error:</strong> {error}</div>}

        {!loading && !error && data && (
          <div style={s.card}>
            {/* Card header */}
            <div style={s.cardHeader}>
              <h2 style={s.cardTitle}>
                Team scorecard — {periodLabel}
              </h2>
              <div style={s.periodTabs}>
                {['week', 'month'].map((p) => (
                  <button
                    key={p}
                    style={{
                      ...s.periodTab,
                      backgroundColor: period === p ? '#534AB7' : '#fff',
                      color: period === p ? '#fff' : '#374151',
                      border: period === p ? '1px solid #534AB7' : '1px solid #e8e6e0',
                    }}
                    onClick={() => setPeriod(p)}
                  >
                    {p === 'week' ? 'This week' : 'This month'}
                  </button>
                ))}
              </div>
            </div>

            {/* Stat tiles */}
            <div style={s.tileGrid}>
              <div style={s.tile}>
                <div style={{ ...s.tileNumber, color: '#534AB7' }}>{data.totals.calls}</div>
                <div style={s.tileLabel}>total calls logged</div>
              </div>
              <div style={s.tile}>
                <div style={{ ...s.tileNumber, color: '#16a34a' }}>{data.totals.reached}</div>
                <div style={s.tileLabel}>prospects reached</div>
              </div>
              <div style={s.tile}>
                <div style={{ ...s.tileNumber, color: '#534AB7' }}>{data.totals.showings}</div>
                <div style={s.tileLabel}>showings logged</div>
              </div>
              <div style={{ ...s.tile, backgroundColor: data.totals.overdue > 0 ? '#fffbeb' : '#f9fafb' }}>
                <div style={{ ...s.tileNumber, color: data.totals.overdue > 0 ? '#d97706' : '#6b7280' }}>
                  {data.totals.overdue}
                </div>
                <div style={s.tileLabel}>overdue follow-ups</div>
              </div>
            </div>

            {/* Broker table */}
            {data.brokerStats.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: '14px', padding: '20px 0' }}>
                No activity logged {period === 'week' ? 'this week' : 'this month'}.
              </p>
            ) : (
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.thLeft}>Broker</th>
                    <th style={s.th}>Calls logged</th>
                    <th style={s.th}>Reached</th>
                    <th style={s.th}>Showings</th>
                    <th style={s.th}>Overdue</th>
                    <th style={s.th}>Queue done</th>
                  </tr>
                </thead>
                <tbody>
                  {data.brokerStats.map((row, i) => {
                    const oc = overdueColor(row.overdue);
                    const cc = clearanceColor(row.clearance);
                    return (
                      <tr key={i}>
                        <td style={s.tdLeft}>{shortName(row.broker)}</td>
                        <td style={s.td}>{row.calls}</td>
                        <td style={{
                          ...s.td,
                          color: row.reached > 0 ? '#16a34a' : '#374151',
                          fontWeight: row.reached > 0 ? '700' : '500',
                        }}>
                          {row.reached}
                        </td>
                        <td style={s.td}>{row.showings}</td>
                        <td style={{ ...s.td, textAlign: 'center' }}>
                          <span style={{ ...s.badge, backgroundColor: oc.bg, color: oc.color }}>
                            {row.overdue}
                          </span>
                        </td>
                        <td style={{ ...s.td, textAlign: 'center' }}>
                          <span style={{ ...s.badge, backgroundColor: cc.bg, color: cc.color }}>
                            {row.clearance}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            {/* Footer */}
            <p style={s.footer}>
              Data pulls live from Monday.com activity log · Updates on save
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
